import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { ChatConversation, ChatDraft, emptyChatDraft } from 'services/api/chat';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import ChannelRail from './ChannelRail';
import ChannelSubscriber from './ChannelSubscriber';
import ChannelView from './ChannelView';

// Exported for InboundReferralPage: the tab-badge rollup excludes the actively-viewed
// channel (resolved from this param), so a watched channel can never flash the tab badge.
export const CHANNEL_PARAM = 'channel';

type Props = {
  // Fed from the page-level useChatConversations mount so a later story's tab badge and
  // this tab read one cache.
  conversations: ChatConversation[];
  isLoading: boolean;
  isError: boolean;
  // Per-conversation draft map, owned by the PAGE: this tab unmounts on tab switches, so
  // drafts held here would die on a trip to the Activity Log.
  drafts: Record<string, ChatDraft>;
  onDraftChange: (conversationId: string, value: ChatDraft) => void;
  // An archived referral reads without a composer (see ChannelView).
  readOnly?: boolean;
};

// The Chat tab: a facility rail (multi-facility only) beside the selected channel's view.
// Selection is the `?channel=<conversationId>` URL param (deep-linkable, survives refresh);
// the derived default is the first channel.
export default function ChatTab({ conversations, isLoading, isError, drafts, onDraftChange, readOnly = false }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Alphabetical is the canonical order (matches the rail); the "first channel" default is
  // the top of it — so a single-facility user lands on their one channel.
  const sorted = useMemo(
    () => [...conversations].sort((a, b) => a.audience.name.localeCompare(b.audience.name)),
    [conversations]
  );

  const channelParam = searchParams.get(CHANNEL_PARAM);
  const selected = sorted.find((conversation) => conversation.id === channelParam) ?? sorted[0];

  // Canonicalize the URL to the resolved channel (absent or stale param → default first),
  // so the selection is deep-linkable and survives refresh without adding history entries.
  useEffect(() => {
    if (!selected || channelParam === selected.id) return;
    const next = new URLSearchParams(searchParams);
    next.set(CHANNEL_PARAM, selected.id);
    setSearchParams(next, { replace: true });
  }, [selected, channelParam, searchParams, setSearchParams]);

  const onSelect = (conversationId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(CHANNEL_PARAM, conversationId);
    setSearchParams(next);
  };

  if (isLoading)
    return (
      <Centered>
        <CircularProgress color={colors.blue100} />
      </Centered>
    );
  if (isError) return <Muted>We couldn’t load this referral’s chat.</Muted>;
  // Zero-channel referrals are a tolerated state: referrals created before the chat
  // feature existed have no conversations (deliberately no backfill), and they age out
  // as those referrals close. Every referral created since gets its channels at creation.
  if (!selected) return <Muted data-testid='chat-no-channels'>No chat channels for this referral.</Muted>;

  const showRail = sorted.length > 1;

  return (
    <Layout data-testid='chat-tab'>
      {/* The open channel's live message stream. Keyed by conversation — mandatory: not for the
          subscription itself (the hook re-subscribes on param changes), but to reset the
          handshake-skip refs so a facility switch doesn't fire a redundant refetch. */}
      <ChannelSubscriber key={`subscriber-${selected.id}`} conversationId={selected.id} />
      {showRail && (
        <RailColumn>
          <ChannelRail conversations={sorted} selectedId={selected.id} onSelect={onSelect} />
        </RailColumn>
      )}
      <ChannelView
        // Key by conversation so message-list/composer state resets cleanly on channel switch.
        key={selected.id}
        conversation={selected}
        // The broadcast toggle rides the same multi-facility condition as the rail.
        canBroadcast={showRail}
        draft={drafts[selected.id] ?? emptyChatDraft}
        onDraftChange={(value) => onDraftChange(selected.id, value)}
        readOnly={readOnly}
      />
    </Layout>
  );
}

// Fills Page.Content exactly (InboundReferralPage's $edgeToEdge zeroes Content's own
// margin/padding for this tab): a normal-flow height:100% row, not position:absolute — the
// rail is a full-bleed white panel flush against the tab bar/sidebar/page bottom (Figma
// "Multi Building Access - Chat View"), and each of its two panes owns its own 24px inset
// instead of Layout supplying a gap.
const Layout = styled.div`
  display: flex;
  height: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    height: auto;
  }

  /* Back to filling Page.Content on a phone. Under the rule above the pane ended wherever the
     messages ended, so the composer floated mid-screen instead of sitting at the bottom of the
     thread where someone replying reaches for it. The 801–900 band still has that — left as it
     was rather than widened past what was asked. */
  @media ${({ theme }) => theme.devices.mobile} {
    height: 100%;
    min-height: 0;
  }
`;

const RailColumn = styled.div`
  flex: 0 0 240px;
  max-width: 240px;
  background: ${colors.white};
  border-right: 1px solid ${colors.black10};
  padding: 24px;
  overflow-y: auto;

  @media (max-width: 900px) {
    flex: 0 1 auto;
    /* Dormant in practice: with the pane at height auto, a percentage max-height has no definite
       container to resolve against, so the rail has never actually been capped here. The mobile
       rule below keeps it that way — giving Layout a height on a phone would bind it for the first
       time, at roughly two channel rows inside their own scroller, which is a change to how chat
       behaves rather than to where the composer sits. */
    max-height: 40%;
    max-width: none;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${colors.black10};
    padding: 16px 24px;
  }

  @media ${({ theme }) => theme.devices.mobile} {
    max-height: none;
  }
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;
